<template>
    <div class="card reservation-card position-relative">
        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="false"
                :showAddNew="true"
                title="Reservations"
                btnTitle="New Reservation"
                @onClickAddNewButton="onClickNew"
            />
        </div>

        <div class="d-flex flex-wrap gap-1 px-2 mt-2">
            <button
                v-for="tab in statusTabs"
                :key="tab.value"
                class="btn btn-sm"
                :class="activeStatus === tab.value ? 'btn-primary' : 'btn-outline-secondary'"
                @click="onClickStatus(tab.value)"
            >
                {{ tab.label }}
            </button>
        </div>

        <div class="row px-2 mt-2">
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="col-form-label">Guest</label>
                <AsyncSelect
                    placeholder="Search guest by name or mobile"
                    v-model="guestContact"
                    :api-service="fetchContactProfiles"
                    :additional-query="{ with_mobile: 1, roles: 'customer' }"
                    :format-label="formatPatientLabel"
                />
            </div>
            <div class="col-12 col-sm-6 col-md-4 col-lg-3">
                <label class="col-form-label">Mobile No</label>
                <input
                    type="text"
                    class="form-control"
                    placeholder="Search by mobile no"
                    v-model.trim="mobileNo"
                />
            </div>
            <div class="col-12 col-sm-6 col-md-4 col-lg-3 d-flex align-items-end">
                <button
                    class="btn btn-primary btn-block waves-effect waves-float waves-light"
                    @click="getReservationList"
                >
                    Go
                </button>
            </div>
        </div>

        <div class="col-12 px-2 mt-2 mb-4">
            <HotelReservationTable
                :tableItems="tableItems"
                @onPay="onClickPay"
                @onEdit="onClickEdit"
                @onCancel="onClickCancel"
                @onCheckin="onClickCheckin"
            />
        </div>

        <div class="px-2 position-absolute bottom-0 mt-4">
            <Pagination :offset="params.offset" @onPageChange="onPageChange"/>
        </div>

        <PayModal
            v-if="showPayModal"
            :reservation="selectedReservation"
            @onClose="showPayModal = false"
            @onSubmit="getReservationList"
        />

        <Loader v-if="loading"/>
    </div>
</template>

<script setup>
import TitleButton from '@/components/atom/TitleButton';
import HotelReservationTable from '@/components/molecule/company/hotel/hotel-reservation/HotelReservationTable.vue';
import PayModal from '@/components/molecule/company/hotel/hotel-reservation/PayModal.vue';
import Loader from '@/components/atom/LoaderComponent';
import Pagination from '@/components/atom/Pagination';
import AsyncSelect from '@/components/molecule/input-field/AsyncSelect.vue';
import { computed, inject, onMounted, reactive, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import handleHotel from '@/services/modules/hotel/hotel';
import handleInventory from '@/services/modules/inventory';
import handleContact from '@/services/modules/contact';
import { useAsyncDropdownHelper } from '@/services/utils/asyncDropdownHelper';

const $router = useRouter();
const $route = useRoute();
const $store = useStore();
const showError = inject('showError');
const showSuccess = inject('showSuccess');
const { fetchHotelReservationList, cancelHotelReservation } = handleHotel();
const { fetchHome } = handleInventory();
const { fetchContactProfiles } = handleContact();
const { formatPatientLabel } = useAsyncDropdownHelper();

const loading = ref(false);
const tableItems = ref([]);
const selectedReservation = ref(null);
const showPayModal = ref(false);
const activeStatus = ref('');
const guestContact = ref('');
const mobileNo = ref('');
const params = reactive({
    offset: 20,
});
const statusTabs = ref([]);

const isPlainStatus = computed(() => {
    if (!activeStatus.value) return false;
    if (activeStatus.value === 'checked_in') return false;
    if (activeStatus.value === 'pending_checkout') return false;
    return true;
});

const getQuery = () => {
    let query = `?company_id=${$route.params.companyId}`;
    if ($route.query.page) query += '&page=' + $route.query.page;
    if (activeStatus.value === 'pending_checkout') query += '&reg_status=checked_in&settlement_status=settled';
    if (activeStatus.value === 'checked_in') query += '&reg_status=checked_in&settlement_status=not_settled';
    if (isPlainStatus.value) query += '&reg_status=' + activeStatus.value;
    if (guestContact.value) query += '&contact_profile_id=' + guestContact.value.id;
    if (mobileNo.value) query += '&mobile_no=' + mobileNo.value;
    query += '&offset=' + params.offset;
    return query;
};

const getReservationList = async () => {
    loading.value = true;
    tableItems.value = [];
    await fetchHotelReservationList(getQuery()).then(res => {
        if (!res.status) {
            $store.commit('resetPagination');
            return;
        }
        tableItems.value = res.data.data;
        setPagination(res.data);
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

const setPagination = (data) => {
    if (!data.data.length) return;
    $store.commit('setPaginationData', {
        records: data.total,
        perPage: data.per_page,
        currentPage: data.current_page,
    });
};

const allowedStatusTabs = ['pending', 'confirmed', 'checked_in', 'cancelled'];

const getStatusTabs = async () => {
    await fetchHome().then(({ data }) => {
        if (!data) return;
        if (!data.hotel_reservation_statuses) return;
        statusTabs.value = data.hotel_reservation_statuses.filter((status) => {
            return allowedStatusTabs.includes(status.value);
        }).map((status) => {
            return { label: status.name, value: status.value };
        });
        if (!activeStatus.value && statusTabs.value.length) activeStatus.value = statusTabs.value[0].value;
    });
};

const onClickStatus = (status) => {
    activeStatus.value = status;
    const routeQuery = Object.assign({}, $route.query, { reg_status: status });
    $router.replace({ path: $route.path, query: routeQuery });
    getReservationList();
};

const restoreActiveStatus = () => {
    if (!$route.query.reg_status) return;
    activeStatus.value = $route.query.reg_status;
};

const onClickNew = () => {
    $router.push({
        name: 'hotel-reservation-create',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId,
        },
        query: {
            reg_status: activeStatus.value,
            page: $route.query.page,
        },
    });
};

const onClickEdit = (item) => {
    if (item.registration_status === 'checked_in') {
        onClickCheckin(item);
        return;
    }
    $router.push({
        name: 'hotel-reservation-create',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId,
        },
        query: {
            reservation_id: item.id,
            reg_status: activeStatus.value,
            page: $route.query.page,
        },
    });
};

const onClickCheckin = (item) => {
    $router.push({
        name: 'hotel-checkin',
        params: {
            companyId: $route.params.companyId,
            moduleId: $route.params.moduleId,
            menuId: $route.params.menuId,
            pageId: $route.params.pageId,
            ipdRegisterId: item.id,
        },
    });
};

const onClickPay = (item) => {
    selectedReservation.value = item;
    showPayModal.value = true;
};

const onClickCancel = async (item) => {
    if (!confirm('Cancel this reservation?')) return;
    loading.value = true;
    await cancelHotelReservation({ company_id: $route.params.companyId }, item.id).then(res => {
        if (!res.status) {
            showError(res.message);
            return;
        }
        showSuccess(res.message);
        getReservationList();
    }).catch(() => {
        showError('Something went wrong');
    }).finally(() => {
        loading.value = false;
    });
};

const onPageChange = async (page) => {
    const routeQuery = Object.assign({}, $route.query);
    routeQuery.page = page;
    await $router.push({ path: $route.path, query: routeQuery });
    await getReservationList();
};

onMounted(async () => {
    restoreActiveStatus();
    await getStatusTabs();
    await getReservationList();
});
</script>

<style scoped>
.reservation-card {
    min-height: 75vh;
    padding-bottom: 4rem;
}
</style>
